import React from 'react';
import { Typography } from '@mui/material';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import Card from 'components/shared/Card';
import { TechnologyConflict } from 'store/types/Meeting';
import { QuestionResponse, TechnologyType } from 'store/enums/BallotReviewTypes';

interface TechnologyConflictViewCardProps {
  technology: TechnologyConflict;
}

const getResultText = (technology: TechnologyConflict): string | undefined => {
  if (technology.isUsed === QuestionResponse.No) {
    return `No further action is needed for ${
      technology.type === TechnologyType.Patent ? 'patented technology' : 'copyrighted item'
    }.`;
  }

  if (technology.isJustified === QuestionResponse.No) {
    return 'The Document is failed and returned to the TF';
  }

  if (technology.hasLoa === QuestionResponse.No) {
    return '';
  }

  if (technology.hasLoa === QuestionResponse.Yes) {
    return technology.type === TechnologyType.Patent
      ? 'Proceed to check next one, or if this is the last one, Go To Assessment of disclosed copyrighted items.'
      : 'Proceed to assess next one, or if this is the last one, Go To Assessment of disclosed (identified) trademark.';
  }

  if (technology.isJustified === QuestionResponse.Yes && technology.type !== TechnologyType.Patent) {
    return 'Proceed to assess next one, or if this is the last one, Go To Copyright Release Status.';
  }

  return undefined;
};

const TechnologyConflictViewCard: React.FunctionComponent<TechnologyConflictViewCardProps> = ({ technology }) => {
  const resultText = getResultText(technology);
  const isPatent = technology.type === TechnologyType.Patent;
  const itemNumber = technology.itemNumber;
  const title =
    isPatent && itemNumber
      ? `Patent Technology #${itemNumber}`
      : isPatent
      ? 'Patent Technology'
      : itemNumber
      ? `Copyright #${itemNumber}`
      : 'Copyright';

  return (
    <GridContainer>
      <GridItem>
        <Card title={<Typography variant="h6">{title}</Typography>} bordered>
          <GridContainer spacing={1}>
            <GridItem md={2}>
              <Typography>
                <strong>
                  {isPatent
                    ? `Patent Technology${itemNumber ? ` #${itemNumber}` : ''}:`
                    : `Copyright${itemNumber ? ` #${itemNumber}` : ''}:`}
                </strong>{' '}
                {technology.description}
              </Typography>
            </GridItem>

            <GridItem>
              <Typography>
                <strong>
                  {isPatent
                    ? `Is disclosed patented technology${
                        itemNumber ? ` #${itemNumber}` : ''
                      } found to be "might be material" to the Standard/Safety Guideline?: `
                    : `Is disclosed copyrighted item${
                        itemNumber ? ` #${itemNumber}` : ''
                      } used or reproduced in the Standard/Safety Guideline?: `}
                </strong>
                {technology.isUsed ?? ''}
              </Typography>
            </GridItem>
            {technology.isUsed === QuestionResponse.Yes && (
              <GridItem>
                <Typography>
                  <strong>
                    {isPatent
                      ? 'Is the use of this PMPT technically justified?: '
                      : 'Is the use/reproduction of this copyrighted item technically justified?: '}
                  </strong>
                  {technology.isJustified ?? ''}
                </Typography>
              </GridItem>
            )}
            {technology.isJustified === QuestionResponse.Yes && (
              <GridItem>
                <Typography>
                  <strong>
                    {isPatent
                      ? 'Has an LOA for this patented technology been received from every owner?: '
                      : 'Has the copyright release been received from its owner?: '}
                  </strong>
                  {technology.hasLoa ?? ''}
                </Typography>
              </GridItem>
            )}
            {resultText && (
              <GridItem>
                <Typography>
                  <strong>Result:</strong> {resultText}
                </Typography>
              </GridItem>
            )}
          </GridContainer>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default TechnologyConflictViewCard;
